import { Injectable } from "@angular/core";
import { Area, Assertion, AssertionIssue, Component, db, ImageType, Media, SyncableStatus } from "../../../core/database/db";
import { UploadManagerService } from "../../../core/sync/image-sync/upload-manager.service";
import { MediaForCompression } from "../../media/services/image-compression-pipeline.service";
import { LoggerService } from "../../../shared/services/logger.service";

export interface AssertionWithDetails {
  assertion: Assertion;
  component?: Component;
  area?: Area;
  subtext: string;
}


@Injectable({ providedIn: "root" })
export class AssertionService {

  constructor(private uploadManagerService: UploadManagerService, private logger: LoggerService) { }

  public async getAssertionsForJob(jobId: number) {
    const lotId = (await db.inspectionJob
      .where("id")
      .equals(jobId)
      .first())?.lotId ?? 0;

    return db.assertion
      .where("lotId")
      .equals(lotId)
      .toArray();
  }

  public getCompletedAssertions = (assertions: Assertion[], assertionIssues: AssertionIssue[], isCcmInspection: boolean) => assertions.filter(assertion =>
    (isCcmInspection ? assertion.isCcmComplete : assertion.isComplete)
    || !assertion.isApplicable
    || assertionIssues.filter(issue => issue.assertionIdentifier === assertion.identifier).length > 0);

  async saveLooksGoodAssertionDetail(assertion: Assertion, note: string, media: Media[], isCcmInspection: boolean): Promise<Record<string, number>> {
    let addedMediaIds: number[] = [];

    const mediaToDelete = media.filter(m => m.deleted === 1);
    const newMedia = media.filter(m => !m.id);

    const mediaToSave: Media[] = newMedia.map(img => {
      const { file: _f, ...rest } = img as MediaForCompression;
      return rest;
    });

    // syncStatus must be reset to Pending: it is spread in from the existing assertion, which is
    // Completed for anything already synced down, and getDataFromDatabaseToSync only picks up Pending rows.
    const changes = {
      ...assertion,
      updatedAt: new Date(),
      syncStatus: SyncableStatus.Pending
    };
    if (isCcmInspection) {
      changes.ccmNote = note;
    } else {
      changes.note = note;
    }

    await db.transaction("rw", [db.assertion, db.media, db.syncStatus], async () => {
      await db.assertion.update(assertion.id!, changes);
      if (mediaToSave.length > 0) {
        addedMediaIds = await db.media.bulkAdd(mediaToSave, { allKeys: true });
      }
      if (mediaToDelete.length > 0) {
        const mediaIdsToDelete = mediaToDelete.map(m => m.id).filter(id => id !== undefined) as number[];
        await db.softDeleteMedia(mediaIdsToDelete);
      }
    });
    await this.markMediaReadyForUpload(addedMediaIds);

    const mediaFileIdentifierToIdMap = mediaToSave.reduce((acc, media, index) => {
      const mediaId = addedMediaIds[index];
      if (mediaId) {
        acc[media.fileIdentifier] = mediaId;
      }
      return acc;
    }, {} as Record<string, number>);

    return mediaFileIdentifierToIdMap;
  }

  async removeLooksGoodStatus(assertion: Assertion, isCcmInspection: boolean): Promise<void> {
    await db.transaction("rw", [db.assertion, db.media, db.syncStatus], async () => {
      const changes = {
        ...assertion,
        updatedAt: new Date(),
        syncStatus: SyncableStatus.Pending
      };

      if (isCcmInspection) {
        changes.isCcmComplete = false;
        changes.ccmNote = "";
      } else {
        changes.isComplete = false;
        changes.note = "";
      }

      await db.assertion.update(assertion.id!, changes);
      const mediaIds = (await db.media.where("parentIdentifier").equals(assertion.identifier)
        .toArray())
        .filter(m => m.id)
        .map(m => m.id!);
      await db.softDeleteMedia(mediaIds);
    });
  }

  /**
   * Undoes a BQ2/BQ3/BQ4 checklist answer completely: the Pass flag, the N/A flag, the Looks Good
   * note and any photos captured with it, leaving the item unanswered again.
   */
  async clearAssertionResponse(assertion: Assertion, isCcmInspection: boolean): Promise<void> {
    await db.transaction("rw", [db.assertion, db.media, db.syncStatus], async () => {
      // syncStatus is spread in from the existing assertion, which is Completed once synced down, and
      // getDataFromDatabaseToSync only picks up Pending rows.
      const changes = {
        ...assertion,
        isApplicable: true,
        updatedAt: new Date(),
        syncStatus: SyncableStatus.Pending
      };

      if (isCcmInspection) {
        changes.isCcmComplete = false;
        changes.ccmNote = "";
      } else {
        changes.isComplete = false;
        changes.note = "";
      }

      await db.assertion.update(assertion.id!, changes);
      const mediaIds = (await db.media.where("parentIdentifier").equals(assertion.identifier)
        .toArray())
        .filter(m => m.id)
        .map(m => m.id!);
      await db.softDeleteMedia(mediaIds);
    });
  }

  async addAssertions(assertions: Omit<Assertion, "id" | "updatedAt" | "syncStatus">[]): Promise<Assertion[]> {
    const updatedAt = new Date();

    const assertionsForDb = assertions.map(a => ({
      ...a,
      updatedAt,
      syncStatus: SyncableStatus.Pending,
    }));

    const assertionIds = await db.batchBulkOperation("addAssertions", assertionsForDb, batchRecords => db.assertion.bulkAdd(batchRecords, { allKeys: true }), this.logger);

    return assertionsForDb.map((a, index) => ({
      id: assertionIds[index],
      ...a,
    }));
  }

  async updateAssertion(assertion: Omit<Assertion, "updatedAt" | "syncStatus">): Promise<Assertion> {
    if (!!assertion.id === false) {
      throw new Error("Assertion id is required for update");
    }

    const updatedAt = new Date();

    const assertionForDb = {
      ...assertion,
      updatedAt,
      syncStatus: SyncableStatus.Pending,
    };

    await db.assertion.update(assertion.id, assertionForDb);

    return assertionForDb;
  }

  createAssertionWithDetails(assertion: Assertion, components: Component[], areas: Area[], media: Media[], isCcmInspection: boolean): AssertionWithDetails {
    const component = components.find(i => i.id === assertion.componentId);
    const area = areas.find(i => i.id === component?.areaId);

    return {
      assertion,
      component,
      area,
      subtext: this.getLooksGoodSubtext(assertion, area, media, isCcmInspection)
    };
  }

  private getLooksGoodSubtext(assertion: Assertion, area: Area | undefined, media: Media[], isCcmInspection: boolean): string {

    const looksGoodImageType = isCcmInspection ? ImageType.LooksGoodCustomerCareManager : ImageType.LooksGoodConstructionManager;
    const assertionMediaCount = media.filter(am => am.imageType === looksGoodImageType && am.parentIdentifier === assertion.identifier).length;

    const effectiveAssertionNote = isCcmInspection ? assertion.ccmNote : assertion.note;
    const assertionNoteCount = effectiveAssertionNote?.trim().length ? 1 : 0;

    const imageCountText = `${assertionMediaCount > 0 ? `${assertionMediaCount} images` : ""}`;
    const showComma = `${assertionNoteCount > 0 && assertionMediaCount > 0 ? ", " : ""}`;

    if (area) {
      return `${area.name} | ${imageCountText}${assertionNoteCount ? `${showComma}1 note` : ""}`;
    }

    return "";
  }


  private async markMediaReadyForUpload(addedMediaIds: number[]): Promise<void> {
    if (addedMediaIds.length > 0) {
      await this.uploadManagerService.markFilesReadyForUpload(addedMediaIds);
    }
  }
}
